import { TimeZone } from 'common/Constants';
import { getDateTimeDayOfWeek } from 'common/DaysOfWeek';
import { isAvailableOnDate } from 'common/FoodItemAvailability';
import { assertDateStrings, formatDateTimeUrlDate, urlDateToDateTime } from 'common/UrlDate';
import { QueryParams } from 'common/urls/QueryParams';
import { first, flatten, sortBy, sortedUniqBy } from 'lodash';
import { DateTime } from 'luxon';
import QueryString from 'query-string';
import { useCallback, useEffect, useMemo } from 'react';
import { useCartTotals } from 'src/cart/hooks/useCartTotals';
import { useIsDishFirst } from 'src/dish-first/useIsDishFirst';
import { usePersistentDeliveryDates } from 'src/dish-first/usePersistentDeliveryDates';
import { ShefOrderQueryQuery } from 'src/gqlReactTypings.generated.d';
import { UseModalResult } from 'src/shared/hooks/useModal';
import { useDispatch, useSelector } from 'src/store';
import { setActiveCart } from 'src/store/cart/actions';
import { getAdjustedNumDishesAvailable } from 'src/store/cart/utils/capacity';
import { IAvailabilityInfo } from '../components/types';
import { getDefaultDeliveryDate } from '../getDefaultDeliveryDate';
import { isDeliveryDateAvailable } from '../isDeliveryDateAvailable';

const emptyArr = [];

export const useShefMenuDates = (
  urlParams: QueryString.ParsedQuery<string>,
  shefId: string,
  zipModal: UseModalResult,
  cachedZipCode: string | null,
  data: ShefOrderQueryQuery
) => {
  const { isDishFirst } = useIsDishFirst();
  const { validDaysOfWeek, shef, consumerFoodItems: foodItems } = data;
  const { availability, timeZone } = shef;
  const validDaysOfWeekSet = useMemo(() => new Set(validDaysOfWeek), [validDaysOfWeek]);

  const queryParamUrlDates = useMemo(() => {
    const dateParams = flatten([urlParams[QueryParams.DATE] ?? emptyArr]);
    return assertDateStrings(dateParams);
  }, [urlParams]);

  const [deliveryDateUrls, setDeliveryDateUrls] = usePersistentDeliveryDates();
  const deliveryDateUrl = first(deliveryDateUrls);
  const deliveryDate = useMemo(
    () => (deliveryDateUrl ? urlDateToDateTime(deliveryDateUrl, timeZone) : undefined),
    [deliveryDateUrl, timeZone]
  );
  const setDeliveryDate = useCallback(
    (dateTime: DateTime) => setDeliveryDateUrls([formatDateTimeUrlDate(dateTime)]),
    [setDeliveryDateUrls]
  );

  const dispatch = useDispatch();
  const shefFirstCartDeliveryDate = useSelector((state) => {
    const shef = state.cart.shefs.entities[shefId];
    if (!shef || shef.loading) return undefined;
    return urlDateToDateTime(shef.deliveryDate, timeZone);
  });

  const { activeCartItemCount } = useCartTotals();

  // keep active shef in sync with current shef being viewed in shef menu
  useEffect(() => {
    if (deliveryDateUrl || !activeCartItemCount) {
      const cartDeliveryDate = deliveryDateUrl ?? first(availability)?.availabilityDate;
      if (cartDeliveryDate) {
        dispatch(setActiveCart(false, { shefId, deliveryDate: cartDeliveryDate }));
      }
    }
  }, [dispatch, shefId, deliveryDateUrl, availability]);

  const availabilityDateTimes = useMemo(
    () =>
      sortBy(availability, 'availabilityDate').map(({ availabilityDate }) =>
        DateTime.fromISO(availabilityDate, { zone: timeZone })
      ),
    [availability]
  );

  const sortedAvailabilityDates = useMemo(() => {
    const sorted = sortBy(availabilityDateTimes, (item) => item.toMillis());
    const uniqSorted = sortedUniqBy(sorted, (item) => item.toMillis());

    return uniqSorted
      .filter((dateTime) => foodItems.some((a) => isAvailableOnDate(a, dateTime, timeZone)))
      .filter((dateTime) => validDaysOfWeekSet.has(getDateTimeDayOfWeek(dateTime)));
  }, [availabilityDateTimes, validDaysOfWeekSet, foodItems]);

  const availabilityInfo: IAvailabilityInfo[] = useMemo(() => {
    const filteredAvailabilityTimes = availability
      .filter(({ availabilityDate }) => {
        const dateTime = DateTime.fromISO(availabilityDate, { zone: TimeZone.UTC });
        const dayOfWeek = getDateTimeDayOfWeek(dateTime);
        return validDaysOfWeekSet.has(dayOfWeek);
      })
      .map(({ availabilityDate }) => {
        const dateTime = DateTime.fromISO(availabilityDate, { zone: timeZone });
        const numDishesAvailable = getAdjustedNumDishesAvailable({
          shefAvailability: availability,
          rawOrderData: {},
          deliveryDate: availabilityDate,
        });

        return { dateTime, isSoldOut: numDishesAvailable <= 0, numDishesAvailable };
      });

    return filteredAvailabilityTimes;
  }, [availabilityDateTimes, validDaysOfWeekSet, timeZone, availability]);

  // Find datetime object, which pointed from `date` url param.
  // Otherwise choose a default delivery date as shef's next available date.
  useEffect(() => {
    if (!isDishFirst && !isDeliveryDateAvailable({ deliveryDate, availabilityDates: sortedAvailabilityDates })) {
      const defaultDeliveryDate = getDefaultDeliveryDate({
        availabilityDates: sortedAvailabilityDates,
        availabilityInfo,
        userDateFilters: queryParamUrlDates,
        cartDeliveryDate: shefFirstCartDeliveryDate,
      });
      if (defaultDeliveryDate) setDeliveryDate(defaultDeliveryDate);
    }
  }, [isDishFirst, deliveryDate, sortedAvailabilityDates, queryParamUrlDates, availabilityInfo]);

  const onDeliveryDaySelection = useCallback(
    (newDate: DateTime) => (cachedZipCode ? setDeliveryDate(newDate) : zipModal.show()),
    [cachedZipCode, zipModal]
  );

  return {
    availabilityInfo,
    deliveryDate,
    onDeliveryDaySelection,
  };
};
